import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translateFieldError } from '@src/validation/translateFieldError';
import { validateFieldError } from '@src/validation/validateField';

type StringFields = Record<string, string>;

export type FieldBind = {
  value: string;
  error?: string;
  onValueChange: (value: string) => void;
  onBlur: () => void;
};

/**
 * Bind a santa-utils model (`LoginInput`, `RegisterInput`, …) to controlled
 * fields. The `@validate` schema on each property is the rule for that field.
 *
 * Spread `field('email')` onto Input. Blur checks that field; `submit` checks
 * every field and only then runs the handler.
 */
export function useValidatedForm<T extends StringFields>(
  Model: new () => object,
  initial: T,
  siblings?: Partial<Record<keyof T, string[]>>,
) {
  const { t } = useTranslation();
  const model = useMemo(() => new Model(), [Model]);
  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const valuesRef = useRef(values);
  useLayoutEffect(() => {
    valuesRef.current = values;
  }, [values]);

  function messageFor(name: keyof T, nextValues: T): string | undefined {
    const error = validateFieldError(
      model,
      String(name),
      nextValues,
      (siblings?.[name] ?? []).map(String),
    );
    return error ? translateFieldError(error, t) : undefined;
  }

  function change(name: keyof T) {
    return (value: string) => {
      setValues((current) => ({ ...current, [name]: value }));
      setErrors((current) => ({ ...current, [name]: undefined }));
    };
  }

  function blur(name: keyof T) {
    return () => {
      const message = messageFor(name, valuesRef.current);
      setErrors((current) => ({ ...current, [name]: message }));
    };
  }

  function field(name: keyof T): FieldBind {
    return {
      value: values[name],
      error: errors[name],
      onValueChange: change(name),
      onBlur: blur(name),
    };
  }

  function validateAll(nextValues: T = valuesRef.current): boolean {
    const next: Partial<Record<keyof T, string>> = {};
    let ok = true;
    for (const name of Object.keys(nextValues) as (keyof T)[]) {
      const message = messageFor(name, nextValues);
      if (message) {
        ok = false;
        next[name] = message;
      }
    }
    setErrors(next);
    return ok;
  }

  async function submit(handler: (next: T) => Promise<void> | void): Promise<boolean> {
    const nextValues = valuesRef.current;
    if (!validateAll(nextValues)) {
      return false;
    }
    await handler(nextValues);
    return true;
  }

  const filled = Object.values(values).every((value) => value.trim() !== '');

  return { values, errors, filled, field, change, blur, validateAll, submit };
}
